import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Navigate } from 'react-router-dom';
import { getBridgeHealth, GetBridgeHealthOutputType, getBridgeFeedAudit, GetBridgeFeedAuditOutputType, exportSafeSummary } from 'zite-endpoints-sdk';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Activity, CheckCircle, XCircle, RefreshCw, Server, Key, Globe, Loader2, Shield, Download, Clock, FileText, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';

type AuditEntry = GetBridgeFeedAuditOutputType['entries'][0];

function StatusBadge({ ok }: { ok: boolean }) {
  return ok
    ? <Badge variant="secondary" className="bg-primary/15 text-primary gap-1"><CheckCircle className="w-3 h-3" /> OK</Badge>
    : <Badge variant="secondary" className="bg-destructive/15 text-destructive gap-1"><XCircle className="w-3 h-3" /> Issue</Badge>;
}

function ConfigRow({ icon: Icon, label, detail, ok }: { icon: React.ElementType; label: string; detail: string; ok: boolean }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
      </div>
      <StatusBadge ok={ok} />
    </div>
  );
}

function FeedCard({ title, version, description }: { title: string; version: string; description: string }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <Badge variant="secondary" className="text-xs">{version}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const ts = entry.timestamp ? formatDistanceToNow(parseISO(entry.timestamp), { addSuffix: true }) : '—';
  const sevColor = entry.severity === 'Warning' ? 'text-amber-500' : entry.severity === 'Blocked' || entry.severity === 'Critical' ? 'text-destructive' : 'text-muted-foreground';

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <Clock className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${sevColor}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{entry.action}</p>
        <p className="text-xs text-muted-foreground truncate">{entry.target} · {entry.actorEmail}</p>
        {entry.details && <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{entry.details}</p>}
      </div>
      <span className="text-xs text-muted-foreground shrink-0">{ts}</span>
    </div>
  );
}

function ExcludedFieldsList() {
  const excluded = [
    'Wages & payslips', 'Bank details (sort code, account number, bank name)',
    'NI number & tax information', 'Contracts & employment documents',
    'Disciplinary & private HR notes', 'Private documents',
    'Journal entries', 'Performance reviews', 'Edit request content',
    'Addresses & emergency contacts', 'Planday ID & Xero ID',
    'Holiday balances & pension/probation status', 'Xero payroll data',
  ];

  return (
    <div className="border border-dashed border-border rounded-lg p-4 bg-muted/20">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-primary" />
        <p className="text-sm font-semibold text-foreground">Privacy Exclusion Policy</p>
      </div>
      <p className="text-xs text-muted-foreground mb-2">
        The following fields are <strong>never sent</strong> to BMS or Daily Ops via any bridge feed:
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {excluded.map(f => (
          <div key={f} className="flex items-center gap-1.5">
            <XCircle className="w-3 h-3 text-destructive shrink-0" />
            <span className="text-xs text-muted-foreground">{f}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BridgeHealth() {
  const { user } = useAuth();
  const [data, setData] = useState<GetBridgeHealthOutputType | null>(null);
  const [audit, setAudit] = useState<GetBridgeFeedAuditOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setRefreshing(true);
    Promise.all([
      getBridgeHealth({}).catch(() => null),
      getBridgeFeedAudit({ limit: 20 }).catch(() => null),
    ]).then(([h, a]) => {
      if (h) setData(h);
      if (a) setAudit(a);
    }).finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await exportSafeSummary({ activeOnly: true });
      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `safe-staff-summary-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  if (user?.role !== 'Admin' && user?.role !== 'Owner') return <Navigate to="/" replace />;

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
    </div>
  );

  return (
    <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-xl font-bold text-foreground">Bridge Feed Health</h1>
            <p className="text-sm text-muted-foreground">Feed status, schema versions, audit trail and manual export</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="health">
        <TabsList className="mb-4">
          <TabsTrigger value="health">Connection</TabsTrigger>
          <TabsTrigger value="feeds">Feed Schema</TabsTrigger>
          <TabsTrigger value="audit">Audit Trail</TabsTrigger>
          <TabsTrigger value="policy">Privacy Policy</TabsTrigger>
        </TabsList>

        {/* ── Connection Health ── */}
        <TabsContent value="health">
          {data && (
            <div className="space-y-3">
              <ConfigRow icon={Globe} label="BMS Hub URL" detail={data.configuredUrl || 'Not configured'} ok={data.bmsUrlConfigured} />
              <ConfigRow icon={Key} label="Bridge API Secret" detail={data.bridgeSecretConfigured ? 'Configured (hidden)' : 'Not configured'} ok={data.bridgeSecretConfigured} />
              <ConfigRow icon={Server} label="Bridge Ping" detail={data.pingResult.ok ? `Connected · ${data.pingResult.latencyMs}ms` : data.pingResult.error || 'Failed'} ok={data.pingResult.ok} />

              {(!data.bmsUrlConfigured || !data.bridgeSecretConfigured || !data.pingResult.ok) && (
                <div className="border border-dashed border-border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <p className="text-sm font-semibold text-foreground">Feed Stale or Unavailable</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    If the bridge is unreachable, BMS/Daily Ops will not receive live data. Use the <strong>Export CSV</strong> button above as a manual fallback to send a safe staff summary offline.
                  </p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ── Feed Schema Versions ── */}
        <TabsContent value="feeds">
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" /> Published Feeds
            </h2>

            <FeedCard
              title="bridgeGetOperationalSummary"
              version="v1.0"
              description="Aggregate counts only — active staff, role/dept coverage, training readiness, compliance readiness, onboarding progress, leave pressure, open requests, event staffing. No per-staff records."
            />
            <FeedCard
              title="bridgeGetStaffSummary"
              version="v1.0"
              description="Per-staff safe summary with training/compliance status. Includes displayName, role, department, skills, plandayMatchStatus. Excludes all private HR and pay data."
            />
            <FeedCard
              title="bridgeGetComplianceStatus"
              version="v1.0"
              description="Staff compliance overview — completion counts by staff member (name, role, department only)."
            />
            <FeedCard
              title="bridgeGetEventSetup"
              version="v1.0"
              description="Event setup sheets for BMS — title, date, status only."
            />
            <FeedCard
              title="bridgeGetStaff (legacy)"
              version="v0.9"
              description="Basic active staff list — prefer bridgeGetStaffSummary. Contains name, role, department, skills only."
            />

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">Authentication</p>
              <p className="text-xs text-muted-foreground">
                All bridge feeds require <code className="bg-muted px-1 py-0.5 rounded text-xs">bridgeSecret</code> matching <code className="bg-muted px-1 py-0.5 rounded text-xs">ZITE_BRIDGE_API_SECRET</code>. Requests with an invalid or missing secret receive HTTP 401.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">Write Policy</p>
              <p className="text-xs text-muted-foreground">
                All bridge feeds are <strong>read-only</strong>. No feed writes to Staff Hub records, BMS, or Daily Ops. The only write is an audit log entry for each call.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">Xero Payroll Isolation</p>
              <p className="text-xs text-muted-foreground">
                Xero payroll data is <strong>never exposed via any bridge feed</strong>. Payslips are only accessible to the individual staff member viewing their own record, with ownership verified server-side. Managers cannot access staff pay values. BMS receives no payroll detail.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* ── Audit Trail ── */}
        <TabsContent value="audit">
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Recent Bridge Access
            </h2>

            {audit && audit.entries.length > 0 ? (
              <Card>
                <CardContent className="pt-4 pb-2 divide-y-0">
                  {audit.entries.map(e => <AuditRow key={e.id} entry={e} />)}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">No bridge audit entries found yet. Bridge feeds will log here when consumed.</p>
                </CardContent>
              </Card>
            )}

            {audit && audit.total > 0 && (
              <p className="text-xs text-muted-foreground text-center">
                Showing {audit.entries.length} of {audit.total} entries · Every bridge call is audit-logged with actor, target, and timestamp
              </p>
            )}
          </div>
        </TabsContent>

        {/* ── Privacy Policy ── */}
        <TabsContent value="policy">
          <div className="space-y-4">
            <ExcludedFieldsList />

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">Staff Record Isolation</p>
              <p className="text-xs text-muted-foreground">
                Staff A cannot access Staff B's private records. All authenticated endpoints filter by <code className="bg-muted px-1 py-0.5 rounded text-xs">context.user.id</code> server-side. Payslip endpoints verify Xero employee ownership before returning data.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">Manager Pay Isolation</p>
              <p className="text-xs text-muted-foreground">
                Managers can see team training progress, leave requests, and onboarding status — but <strong>never</strong> staff wages, payslips, bank details, or NI numbers. The payslip endpoint only returns data for <code className="bg-muted px-1 py-0.5 rounded text-xs">context.user.id</code>.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">No Write-Back</p>
              <p className="text-xs text-muted-foreground">
                BMS and Daily Ops cannot write back into Staff Hub records. All bridge endpoints are read-only. There are no inbound webhook triggers that modify staff data from external systems.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4 bg-card">
              <p className="text-sm font-semibold text-foreground mb-1">No Outbound Messages</p>
              <p className="text-xs text-muted-foreground">
                Bridge feeds never send emails, SMS, or trigger payments. The only side-effect of a bridge call is an audit log entry.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
